import {
  type CreateAgentsBatchForAdminEndpointInputAgentInput,
  zCreateAgentsBatchForAdminEndpointInput,
  zCreateAgentsBatchForAdminEndpointInputAgentInput,
} from '@/general/src/agent/routes/createAgentsBatchForAdmin/input.js'
import { agentPermissionsOptions, canManageAgents } from '@/general/src/auth/can.js'
import { getSubscriptionPlanData } from '@/general/src/subscription/utils.shared.js'
import { GeneralLayout } from '@/webapp/src/components/layout/GeneralLayout/index.js'
import { useAppContext } from '@/webapp/src/lib/ctx.js'
import { Checkboxesy, Radiobuttonsy, Textfieldy, useFormy } from '@/webapp/src/lib/formy.js'
import { withPageWrapper } from '@/webapp/src/lib/pageWrapper.js'
import { toast } from '@/webapp/src/lib/toaster.js'
import { trpc } from '@/webapp/src/lib/trpc.js'
import {
  Block,
  Button,
  Buttons,
  FormItem,
  FormItems,
  Segment,
  Segments,
  Table,
} from '@/webapp/src/lib/uninty.components.js'
import { useEffect, useRef, useState } from 'react'
import css from './index.module.scss'

import Papa from 'papaparse'

const parseAgentsCSV = async (file: File): Promise<CreateAgentsBatchForAdminEndpointInputAgentInput[]> => {
  return await new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const rows = results.data as Array<{
            Имя?: string
            Почта?: string
            Телефон?: string
          }>
          const agents: CreateAgentsBatchForAdminEndpointInputAgentInput[] = []
          // const agents = results.data.map((row: any) => ({
          //   name: row['Имя']?.trim() ?? '',
          //   email: row['Почта']?.trim() ?? '',
          //   phone: row['Телефон']?.trim() ?? '',
          // }))
          for (const [index, row] of rows.entries()) {
            const parseResult = zCreateAgentsBatchForAdminEndpointInputAgentInput.safeParse({
              name: row['Имя']?.trim() ?? '',
              email: row['Почта']?.trim() ?? '',
              phone: row['Телефон']?.trim() ?? '',
            })
            const rowNumber = index + 2
            if (!parseResult.success) {
              reject(new Error(`Плохой формат файла на строке ${rowNumber}: ${parseResult.error.message}`))
              return
            }
            const agent = parseResult.data
            agents.push(agent)
          }
          resolve(agents)
        } catch (err) {
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
          reject(err)
        }
      },
      error: (err) => {
        reject(err)
      },
    })
  })
}

export const AgentsBatchUpload = ({
  setAgentsInputs,
}: {
  setAgentsInputs: (agentsInputs: CreateAgentsBatchForAdminEndpointInputAgentInput[]) => void
}) => {
  const inputEl = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  return (
    <FormItem
      label="Файл с агентами"
      hint="Файл должен быть в формате CSV с заголовками: Имя, Почта, Телефон"
      error={error}
    >
      <input
        className={css.fileInput}
        type="file"
        disabled={loading}
        accept="text/csv"
        ref={inputEl}
        onChange={({ target: { files } }) => {
          void (async () => {
            setLoading(true)
            setError(null)
            try {
              if (files?.length) {
                const file = files[0]
                const agentsInputs = await parseAgentsCSV(file)
                setAgentsInputs(agentsInputs)
                setError(null)
              }
            } catch (error_: any) {
              // eslint-disable-next-line no-console
              console.error(error_)
              setError(error_.message)
            } finally {
              setLoading(false)
              if (inputEl.current) {
                inputEl.current.value = ''
              }
            }
          })()
        }}
      />
      <div className={css.buttons}>
        <Buttons>
          <Button type="button" onClick={() => inputEl.current?.click()} loading={loading} disabled={loading}>
            Загрузить
          </Button>
        </Buttons>
      </div>
    </FormItem>
  )
}

export const AdminAgentNewBatchPage = withPageWrapper({
  title: 'Много новых агентов',
  Layout: GeneralLayout,
  authorizedAdminsOnly: true,
  checkAccess: ({ ctx }) => canManageAgents(ctx.me.admin),
})(() => {
  const ctx = useAppContext()
  const createAgentForAdmin = trpc.createAgentsBatchForAdmin.useMutation()
  const formy = useFormy({
    initialValues: {
      organizationSn: '',
      referrerAgentSn: '',
      referralBonusShare: '0.1' as never as number,
      permissions: [],
      subscriptionPlan: 'free',
      paidSubscriptionPlanExpiresAfterDays: 30,
      temporaryRecognitionTokensCount:
        ctx.appConfig.agentSubscriptionPlanMap.free.temporaryRecognitionTokensCountPerPeriod,
      temporaryScenarioTokensCount: ctx.appConfig.agentSubscriptionPlanMap.free.temporaryScenarioTokensCountPerPeriod,
      agentsInputs: [],
    },
    validationSchema: zCreateAgentsBatchForAdminEndpointInput,
    onSubmit: async ({ valuesInput }) => {
      if (!valuesInput.agentsInputs.length) {
        toast.error('Необходимо указать хотя бы одного агента')
        return
      }
      const res = await createAgentForAdmin.mutateAsync({
        ...valuesInput,
      })
      return res.createdAgentsIds.length
    },
    successMessage: ({ submitResult }) => {
      return `Создано ${submitResult} агентов`
    },
  })
  useEffect(() => {
    const subscriptionPlanData = getSubscriptionPlanData({
      agentSubscriptionPlanMap: ctx.appConfig.agentSubscriptionPlanMap,
      subscriptionPlan: formy.values.subscriptionPlan,
    })
    void formy.setFieldValue(
      'temporaryRecognitionTokensCount',
      subscriptionPlanData.temporaryRecognitionTokensCountPerPeriod
    )
    void formy.setFieldValue('temporaryScenarioTokensCount', subscriptionPlanData.temporaryScenarioTokensCountPerPeriod)
  }, [formy.values.subscriptionPlan])
  return (
    <Block fcnw>
      <Segments>
        <Segment title="Много новых агентов" size="m">
          <FormItems as="form" {...formy.formProps}>
            <Textfieldy label="Номер организации" {...formy.getFieldProps('organizationSn')} />
            <Textfieldy label="Номер реферера" {...formy.getFieldProps('referrerAgentSn')} />
            <Textfieldy label="Доля реферальной программы" {...formy.getFieldProps('referralBonusShare')} />
            <Checkboxesy label="Права" options={agentPermissionsOptions} {...formy.getFieldProps('permissions')} />
            <Radiobuttonsy
              {...formy.getFieldProps('subscriptionPlan')}
              label="Тарифный план"
              options={Object.entries(ctx.appConfig.agentSubscriptionPlanMap).map(
                ([key, agentSubscriptionPlanData]) => ({
                  label: agentSubscriptionPlanData.label,
                  value: agentSubscriptionPlanData.name,
                })
              )}
            />
            {formy.values.subscriptionPlan !== 'free' && (
              <>
                <Textfieldy
                  label="Платная подписка истечёт через столько дней"
                  {...formy.getFieldProps('paidSubscriptionPlanExpiresAfterDays')}
                />
              </>
            )}
            <Textfieldy
              label="Количество сгораемых токенов распознавания"
              {...formy.getFieldProps('temporaryRecognitionTokensCount')}
            />
            <Textfieldy
              label="Количество сгораемых токенов сценариев"
              {...formy.getFieldProps('temporaryScenarioTokensCount')}
            />
            <AgentsBatchUpload
              setAgentsInputs={(agentsInputs) => {
                void formy.setFieldValue('agentsInputs', agentsInputs)
              }}
            />
            <Buttons>
              <Button {...formy.buttonProps} type="submit">
                Создать
              </Button>
            </Buttons>
          </FormItems>
        </Segment>
        <Segment title="Агенты" size="m">
          <Table
            records={formy.values.agentsInputs}
            columns={[
              { heading: 'Имя', body: (record) => record.name, width: 200 },
              { heading: 'Почта', body: (record) => record.email, width: 200 },
              { heading: 'Телефон', body: (record) => record.phone, width: 200 },
            ]}
          />
        </Segment>
      </Segments>
    </Block>
  )
})
